import { createPortal } from 'react-dom'

function parsePrice(value) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

export function ProductImageNavIcon({ direction }) {
  const isPrev = direction === 'prev'
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={isPrev ? 'M14.5 5.5 8 12l6.5 6.5' : 'M9.5 5.5 16 12l-6.5 6.5'}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Full-screen product detail modal. `skin` switches the visual variant:
 * "regular" (current design) or "fashion" (editorial, styled via
 * `.product-detail-screen--fashion` CSS overrides). Behavior comes from
 * the shared `useProductCard` hook (`card`).
 */
export function ProductDetailScreen({ skin = 'regular', card }) {
  const {
    detailOpen,
    closeDetail,
    detailLoading,
    detailProduct,
    detailError,
    detailTitleId,
    galleryRef,
    showGalleryControls,
    trackAnimating,
    safeImageIndex,
    dragX,
    handleTrackTransitionEnd,
    loopSlides,
    activeImage,
    stepImage,
    detailImages,
    imageIndex,
    selectDetailImage,
    displayProduct,
    detailPricing,
    descriptionText,
    detailMultiVariant,
    detailVariants,
    selectedVariantId,
    handleDetailVariantClick,
    selectedDetailVariant,
    handleDetailAddToCart,
  } = card

  if (!detailOpen) return null

  const skinClass = skin === 'fashion' ? ' product-detail-screen--fashion' : ''

  return createPortal(
    <div
      className={`product-detail-screen${skinClass}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={detailTitleId}
      aria-busy={detailLoading}
    >
      <button
        type="button"
        className="product-detail-close"
        onClick={closeDetail}
        aria-label="Закрыть"
      >
        ×
      </button>
      {detailLoading && !detailProduct ? (
        <div className="product-detail-loading" role="status" aria-live="polite">
          <div className="product-detail-loading-ring" aria-hidden="true" />
          <span>Загрузка…</span>
        </div>
      ) : (
        <div className="product-detail-layout">
          <div className="product-detail-gallery">
            <div
              ref={galleryRef}
              className={`product-detail-image-wrap${showGalleryControls ? ' product-detail-image-wrap--gallery' : ''}`}
            >
              {showGalleryControls ? (
                <div
                  className={`product-detail-image-track${trackAnimating ? ' product-detail-image-track--animating' : ''}`}
                  style={{
                    transform: `translate3d(calc(${-(safeImageIndex + 1) * 100}% + ${dragX}px), 0, 0)`,
                  }}
                  onTransitionEnd={handleTrackTransitionEnd}
                >
                  {loopSlides.map((img, i) => (
                    <div
                      key={`detail-slide-${i}`}
                      className="product-detail-image-slide"
                      aria-hidden={i === 0 || i === loopSlides.length - 1}
                    >
                      <img
                        src={img.url}
                        alt=""
                        className="product-detail-image"
                        draggable={false}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <img
                  src={activeImage}
                  alt=""
                  className="product-detail-image"
                  draggable={false}
                />
              )}
              {showGalleryControls ? (
                <>
                  <button
                    type="button"
                    className="product-image-nav product-image-nav--prev product-image-nav--desktop"
                    onClick={(e) => stepImage(-1, e)}
                    aria-label="Предыдущее фото"
                  >
                    <ProductImageNavIcon direction="prev" />
                  </button>
                  <button
                    type="button"
                    className="product-image-nav product-image-nav--next product-image-nav--desktop"
                    onClick={(e) => stepImage(1, e)}
                    aria-label="Следующее фото"
                  >
                    <ProductImageNavIcon direction="next" />
                  </button>
                  <div className="product-image-dots" aria-hidden="true">
                    {detailImages.map((img, i) => (
                      <span
                        key={`detail-${img.url}-${i}`}
                        className={`product-image-dot${i === imageIndex ? ' product-image-dot--active' : ''}`}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {showGalleryControls ? (
              <div className="product-detail-thumbs" role="tablist" aria-label="Фотографии товара">
                {detailImages.map((img, i) => (
                  <button
                    key={`thumb-${img.url}-${i}`}
                    type="button"
                    role="tab"
                    aria-selected={i === imageIndex}
                    className={`product-detail-thumb${i === imageIndex ? ' product-detail-thumb--active' : ''}`}
                    onClick={() => selectDetailImage(i)}
                  >
                    <img src={img.url} alt="" draggable={false} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="product-detail-panel">
            <div className="product-detail-panel-body">
              {detailError ? (
                <div className="product-detail-error" role="alert">
                  {detailError}
                </div>
              ) : null}
              <h2 id={detailTitleId} className="product-detail-title">
                {displayProduct.name}
              </h2>
              <div className="product-detail-prices">
                {detailPricing.oldPrice != null && (
                  <span className="product-price-old" aria-label="Прежняя цена">
                    {detailPricing.oldPrice.toFixed(2)} сом
                  </span>
                )}
                <span className="product-price product-detail-price-current">
                  {Number.isFinite(detailPricing.currentPrice)
                    ? `${detailPricing.pricePrefix}${detailPricing.currentPrice.toFixed(2)} сом${detailPricing.priceSuffix || ''}`
                    : '—'}
                </span>
              </div>
              {descriptionText &&
                <div className="product-detail-description">
                  {descriptionText}
                </div>}
              {detailMultiVariant ? (
                <div className="product-detail-variants">
                  {detailVariants.map((variant) => {
                    const vPrice = parsePrice(variant.price)
                    const vOld = parsePrice(variant.oldPrice)
                    const showOld = Number.isFinite(vPrice) && Number.isFinite(vOld) && vOld > vPrice
                    const variantId = variant.id ?? variant._id
                    const selected = selectedVariantId === variantId
                    return (
                      <button
                        key={variantId}
                        type="button"
                        className={`product-variant-option${selected ? ' product-variant-option--selected' : ''}`}
                        onClick={() => handleDetailVariantClick(variant)}
                      >
                        <span className="product-variant-option-title">
                          {String(variant.title ?? '').trim() || 'Обычный'}
                        </span>
                        <span className="product-variant-option-price">
                          {showOld ? (
                            <span className="product-price-old">{vOld.toFixed(2)}</span>
                          ) : null}
                          {Number.isFinite(vPrice) ? `${vPrice.toFixed(2)} сом` : '—'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="product-detail-add"
              onClick={handleDetailAddToCart}
              disabled={!selectedDetailVariant}
            >
              В корзину
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}

export function VariantPickerPortal({ card }) {
  const { product, variantPickerOpen, setVariantPickerOpen, variants, handlePickVariant } = card

  if (!variantPickerOpen) return null

  return createPortal(
    <div
      className="product-detail-overlay"
      onMouseDown={() => setVariantPickerOpen(false)}
      role="presentation"
    >
      <div
        className="product-detail-dialog product-variant-picker-dialog"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Выберите вариант: ${product.name ?? 'товар'}`}
      >
        <button
          type="button"
          className="product-detail-close"
          onClick={() => setVariantPickerOpen(false)}
          aria-label="Закрыть"
        >
          ×
        </button>
        <div className="product-detail-dialog-scroll">
          <h2 className="product-detail-title">{product.name}</h2>
          <p className="product-variant-picker-hint">Выберите вариант</p>
          <div className="product-detail-variants">
            {variants.map((variant) => {
              const vPrice = parsePrice(variant.price)
              const vOld = parsePrice(variant.oldPrice)
              const showOld = Number.isFinite(vPrice) && Number.isFinite(vOld) && vOld > vPrice
              return (
                <button
                  key={variant.id ?? variant._id}
                  type="button"
                  className="product-variant-option"
                  onClick={() => handlePickVariant(variant)}
                >
                  <span className="product-variant-option-title">
                    {String(variant.title ?? '').trim() || 'Обычный'}
                  </span>
                  <span className="product-variant-option-price">
                    {showOld ? (
                      <span className="product-price-old">{vOld.toFixed(2)}</span>
                    ) : null}
                    {Number.isFinite(vPrice) ? `${vPrice.toFixed(2)} сом` : '—'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export function FlyToCartBubbles({ items }) {
  if (!items?.length) return null

  return createPortal(
    <>
      {items.map((fly) => (
        <div
          key={fly.id}
          className="cart-fly-bubble"
          style={{
            left: `${fly.fromX}px`,
            top: `${fly.fromY}px`,
            '--fly-dx': `${fly.dx}px`,
            '--fly-dy': `${fly.dy}px`,
          }}
          aria-hidden="true"
        >
          {fly.image ? (
            <img src={fly.image} alt="" className="cart-fly-bubble-image" />
          ) : (
            <span className="cart-fly-bubble-dot" />
          )}
        </div>
      ))}
    </>,
    document.body
  )
}
